import React, { useState, useEffect, useMemo } from "react";
import {
  Drawer,
  Input,
  Select,
  Button,
  Card,
  Tag,
  Space,
  Typography,
  Row,
  Col,
  Empty,
  Badge,
  Spin,
  Modal,
  Form,
  InputNumber,
  message,
  Tooltip,
  Divider,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  CompassOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  EnvironmentOutlined,
  FilterOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  FolderOpenOutlined,
} from "@ant-design/icons";
import activityCatalogService from "@/services/activityCatalogService";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const CATEGORIES = [
  "All Categories",
  "Sightseeing",
  "Adventure",
  "Water Sports",
  "Cultural",
  "Entry Ticket",
  "Transfer",
  "Cruise",
  "Day Tour",
  "Entertainment",
  "Other",
];

const ActivitySearchTab = ({
  open,
  onClose,
  days = [],
  updateDay,
  defaultDestination = "",
}) => {
  const [activities, setActivities] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDestination, setSelectedDestination] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("All Categories");

  // Selected target day ID for insertion
  const [targetDayId, setTargetDayId] = useState(days[0]?.id || "");

  // Quick Create Modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createForm] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  // Sync default target day if days change
  useEffect(() => {
    if (days.length > 0 && (!targetDayId || !days.some((d) => d.id === targetDayId))) {
      setTargetDayId(days[0].id);
    }
  }, [days]);

  // Set default destination if provided
  useEffect(() => {
    if (defaultDestination && defaultDestination !== "all") {
      setSelectedDestination(defaultDestination);
    }
  }, [defaultDestination]);

  // Fetch destinations list
  const fetchDestinations = async () => {
    try {
      const res = await activityCatalogService.getDestinations();
      if (res?.data) {
        setDestinations(res.data);
      }
    } catch (err) {
      console.error("Error fetching destinations:", err);
    }
  };

  // Fetch activities from backend
  const fetchActivities = async () => {
    try {
      setLoading(true);
      const params = {
        search: searchTerm,
        destination: selectedDestination !== "all" ? selectedDestination : undefined,
        category: selectedCategory !== "All Categories" ? selectedCategory : undefined,
        limit: 100,
      };
      const res = await activityCatalogService.getActivities(params);
      if (res?.data) {
        setActivities(res.data);
      }
    } catch (err) {
      console.error("Error fetching activity catalog:", err);
      message.error("Failed to search activity catalog");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchDestinations();
      fetchActivities();
    }
  }, [open, selectedDestination, selectedCategory]);

  const handleSearch = () => {
    fetchActivities();
  };

  // Add activity to the selected day
  const handleAddActivityToDay = (activity, dayId) => {
    const targetDay = days.find((d) => d.id === dayId);
    if (!targetDay) {
      message.warning("Please select a valid itinerary day.");
      return;
    }

    const currentActivities = targetDay.activities || [];
    const newActivityItem = {
      title: activity.title,
      category: activity.category || "Sightseeing",
      quantity: 1,
      price: Number(activity.price) || 0,
      total: Number(activity.price) || 0,
      duration: activity.duration || "2-3 Hours",
      notes: activity.description || "",
      inclusions: activity.inclusions || [],
      location: activity.location || "",
      imageUrl: activity.imageUrl || activity.image || "",
      image: activity.imageUrl || activity.image || "",
      catalogId: activity._id,
    };

    updateDay(dayId, {
      activities: [...currentActivities, newActivityItem],
    });

    const dayNumber = days.findIndex((d) => d.id === dayId) + 1;
    message.success(
      `Added "${activity.title}" to Day ${dayNumber} (${targetDay.title || "Day " + dayNumber})`
    );
  };

  // Quick create custom activity handler
  const handleQuickCreateActivity = async (values) => {
    try {
      setSubmitting(true);
      const res = await activityCatalogService.createActivity({
        ...values,
        inclusions: values.inclusions
          ? values.inclusions.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
      });
      if (res?.success) {
        message.success("New activity added to Master Catalog!");
        setCreateModalOpen(false);
        createForm.resetFields();
        fetchActivities();
        fetchDestinations();

        // Optionally add it directly to target day
        if (res.data && targetDayId) {
          handleAddActivityToDay(res.data, targetDayId);
        }
      }
    } catch (err) {
      console.error("Failed to create activity:", err);
      message.error(err?.response?.data?.message || "Failed to create activity");
    } finally {
      setSubmitting(false);
    }
  };

  const activeDayObj = days.find((d) => d.id === targetDayId);
  const activeDayIndex = days.findIndex((d) => d.id === targetDayId) + 1;

  return (
    <>
      <Drawer
        title={
          <div style={{ display: "flex", alignItems: "center", justifyBetween: "space-between", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <CompassOutlined style={{ fontSize: 22, color: "#1677ff" }} />
              <div>
                <Title level={5} style={{ margin: 0, fontWeight: 700 }}>
                  Search & Add Activities
                </Title>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Search master activity catalog and insert directly into itinerary days
                </Text>
              </div>
            </div>
          </div>
        }
        placement="right"
        width={680}
        onClose={onClose}
        open={open}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{ borderRadius: 8 }}
            onClick={() => {
              createForm.setFieldsValue({
                destination: selectedDestination !== "all" ? selectedDestination : defaultDestination || "",
                category: "Sightseeing",
                currency: "INR",
              });
              setCreateModalOpen(true);
            }}
          >
            Create New Activity
          </Button>
        }
      >
        {/* TOP TARGET DAY SELECTOR & FILTERS */}
        <Card
          size="small"
          style={{
            marginBottom: 16,
            background: "#f8fafc",
            borderColor: "#e2e8f0",
            borderRadius: 14,
          }}
          bodyStyle={{ padding: 14 }}
        >
          <div style={{ marginBottom: 12 }}>
            <Text strong style={{ color: "#334155", display: "block", marginBottom: 6 }}>
              🎯 Target Day in Itinerary:
            </Text>
            <Select
              style={{ width: "100%" }}
              value={targetDayId}
              onChange={(val) => setTargetDayId(val)}
              placeholder="Select Day to Add Activity"
              size="large"
            >
              {days.map((d, index) => (
                <Option key={d.id} value={d.id}>
                  <strong>Day {index + 1}:</strong> {d.title || `Day ${index + 1}`} ({d.activities?.length || 0} activities added)
                </Option>
              ))}
            </Select>
          </div>

          <Divider style={{ margin: "10px 0" }} />

          {/* SEARCH & FILTERS */}
          <Row gutter={[10, 10]}>
            <Col xs={24} sm={12}>
              <Input
                placeholder="Search by title, location, keywords..."
                prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onPressEnter={handleSearch}
                allowClear
              />
            </Col>

            <Col xs={12} sm={6}>
              <Select
                style={{ width: "100%" }}
                value={selectedDestination}
                onChange={(val) => setSelectedDestination(val)}
                placeholder="Destination"
              >
                <Option value="all">🌍 All Destinations</Option>
                {destinations.map((d) => (
                  <Option key={d} value={d}>
                    📍 {d}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={12} sm={6}>
              <Select
                style={{ width: "100%" }}
                value={selectedCategory}
                onChange={(val) => setSelectedCategory(val)}
                placeholder="Category"
              >
                {CATEGORIES.map((cat) => (
                  <Option key={cat} value={cat}>
                    {cat}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Found <strong>{activities.length}</strong> matching activities
            </Text>
            <Button
              size="small"
              type="link"
              icon={<ReloadOutlined />}
              onClick={fetchActivities}
            >
              Refresh
            </Button>
          </div>
        </Card>

        {/* ACTIVITY LISTING */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <Spin size="large" tip="Searching Activity Catalog..." />
          </div>
        ) : activities.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div>
                <Paragraph style={{ margin: 0, fontWeight: 500 }}>No activities found in catalog</Paragraph>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Try adjusting your search query or create a new activity directly.
                </Text>
              </div>
            }
          >
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                createForm.setFieldsValue({
                  destination: selectedDestination !== "all" ? selectedDestination : defaultDestination || "",
                  category: "Sightseeing",
                  currency: "INR",
                });
                setCreateModalOpen(true);
              }}
            >
              Add New Activity
            </Button>
          </Empty>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {activities.map((act) => {
              const isAlreadyInTargetDay = activeDayObj?.activities?.some(
                (a) => a.catalogId === act._id || a.title?.toLowerCase() === act.title?.toLowerCase()
              );

              return (
                <Card
                  key={act._id}
                  size="small"
                  hoverable
                  style={{
                    borderRadius: 12,
                    borderColor: isAlreadyInTargetDay ? "#b7eb8f" : "#e2e8f0",
                    background: isAlreadyInTargetDay ? "#f6ffed" : "#ffffff",
                    transition: "all 0.2s ease",
                  }}
                  bodyStyle={{ padding: 14 }}
                >
                  <Row gutter={12} align="middle">
                    {(act.imageUrl || act.image) && (
                      <Col xs={24} sm={6}>
                        <img
                          src={act.imageUrl || act.image}
                          alt={act.title}
                          style={{
                            width: "100%",
                            height: 85,
                            objectFit: "cover",
                            borderRadius: 8,
                            border: "1px solid #e2e8f0",
                          }}
                        />
                      </Col>
                    )}

                    <Col xs={24} sm={(act.imageUrl || act.image) ? 11 : 16}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <Tag color="blue" style={{ borderRadius: 6, fontWeight: 600 }}>
                          📍 {act.destination}
                        </Tag>
                        <Tag color="purple" style={{ borderRadius: 6 }}>
                          {act.category || "Sightseeing"}
                        </Tag>
                        {act.duration && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            <ClockCircleOutlined style={{ marginRight: 4 }} />
                            {act.duration}
                          </Text>
                        )}
                      </div>

                      <Title level={5} style={{ margin: "2px 0 6px 0", color: "#0f172a", fontWeight: 700 }}>
                        {act.title}
                      </Title>

                      {act.description && (
                        <Paragraph
                          ellipsis={{ rows: 2, expandable: true, symbol: "more" }}
                          style={{ margin: 0, fontSize: 13, color: "#64748b" }}
                        >
                          {act.description}
                        </Paragraph>
                      )}

                      {act.inclusions && act.inclusions.length > 0 && (
                        <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {act.inclusions.slice(0, 3).map((inc, i) => (
                            <Tag key={i} color="green" style={{ fontSize: 11, borderRadius: 4 }}>
                              ✓ {inc}
                            </Tag>
                          ))}
                          {act.inclusions.length > 3 && (
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              +{act.inclusions.length - 3} more
                            </Text>
                          )}
                        </div>
                      )}
                    </Col>

                    <Col xs={24} sm={8} style={{ textAlign: "right", marginTop: 8 }}>
                      <div style={{ marginBottom: 8 }}>
                        <Text style={{ fontSize: 12, color: "#64748b" }}>Rate per pax</Text>
                        <div style={{ fontSize: 18, fontWeight: 800, color: "#059669" }}>
                          ₹{(act.price || 0).toLocaleString()}
                        </div>
                      </div>

                      <Button
                        type={isAlreadyInTargetDay ? "default" : "primary"}
                        icon={isAlreadyInTargetDay ? <CheckCircleOutlined /> : <PlusOutlined />}
                        style={{
                          borderRadius: 8,
                          backgroundColor: isAlreadyInTargetDay ? "#f6ffed" : undefined,
                          borderColor: isAlreadyInTargetDay ? "#52c41a" : undefined,
                          color: isAlreadyInTargetDay ? "#52c41a" : undefined,
                          width: "100%",
                        }}
                        onClick={() => handleAddActivityToDay(act, targetDayId)}
                      >
                        {isAlreadyInTargetDay ? `Added to Day ${activeDayIndex}` : `Add to Day ${activeDayIndex}`}
                      </Button>
                    </Col>
                  </Row>
                </Card>
              );
            })}
          </div>
        )}
      </Drawer>

      {/* QUICK CREATE ACTIVITY MODAL */}
      <Modal
        title="✨ Add New Activity to Master Catalog"
        open={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        onOk={() => createForm.submit()}
        confirmLoading={submitting}
        okText="Save & Add to Catalog"
        destroyOnClose
        width={560}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleQuickCreateActivity}
          style={{ marginTop: 12 }}
        >
          <Row gutter={12}>
            <Col span={16}>
              <Form.Item
                name="title"
                label="Activity Title"
                rules={[{ required: true, message: "Title is required" }]}
              >
                <Input placeholder="e.g. Scuba Diving & Parasailing at Grand Island" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="destination"
                label="Destination"
                rules={[{ required: true, message: "Destination is required" }]}
              >
                <Input placeholder="e.g. Goa, Dubai" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="category" label="Category" initialValue="Sightseeing">
                <Select>
                  {CATEGORIES.filter((c) => c !== "All Categories").map((c) => (
                    <Option key={c} value={c}>
                      {c}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="duration" label="Duration" initialValue="2-3 Hours">
                <Input placeholder="e.g. Full Day, 3 Hours" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="price"
                label="Price (₹ / pax)"
                rules={[{ required: true, message: "Price required" }]}
              >
                <InputNumber style={{ width: "100%" }} min={0} placeholder="1500" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="location" label="Location / Pickup Point">
            <Input placeholder="e.g. Calangute Beach, North Goa" />
          </Form.Item>

          <Form.Item name="inclusions" label="Inclusions (Comma separated)">
            <Input placeholder="e.g. Hotel Transfer, Equipment, Life Jacket, Refreshments" />
          </Form.Item>

          <Form.Item name="description" label="Description / Highlights">
            <Input.TextArea rows={3} placeholder="Brief summary of what travelers will experience..." />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ActivitySearchTab;
